import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Avatar, List, ListItem, ListItemAvatar,
  ListItemText, Chip, Skeleton, Alert, Button, Tabs, Tab, Pagination,
  FormControl, Select, MenuItem, InputLabel, Grid, Card, CardContent,
  Divider, Stack, Tooltip, LinearProgress, Badge, IconButton, Fade, Grow,
  CircularProgress
} from '@mui/material';
import {
  EmojiEvents, TrendingUp, Star, FilterList, Refresh,
  Schedule, CalendarToday, AllInclusive, PersonAdd,
  EmojiNature, Recycling, Security, WorkspacePremium
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getLeaderboard, getUserStats } from '../../api/rewards';
import LeaderboardCard from '../Leaderboard/LeaderboardCard';

const RewardsLeaderboard = () => {
  const { user, isAuthenticated } = useAuth();

  // State management (using the EXACT working pattern from LeaderboardPage.jsx)
  const [currentTab, setCurrentTab] = useState(0);
  const [leaderboardData, setLeaderboardData] = useState({
    weekly: { data: [], loading: false, error: null, page: 1, totalPages: 0, totalElements: 0 },
    monthly: { data: [], loading: false, error: null, page: 1, totalPages: 0, totalElements: 0 },
    alltime: { data: [], loading: false, error: null, page: 1, totalPages: 0, totalElements: 0 }
  });
  const [userStats, setUserStats] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('totalPoints,desc');

  // Tab configuration (matching working pattern)
  const tabs = [
    {
      label: 'Weekly',
      period: 'weekly',
      icon: <Schedule fontSize="small" />,
      color: '#66bb6a',
      description: 'This week\'s eco champions'
    },
    {
      label: 'Monthly',
      period: 'monthly',
      icon: <CalendarToday fontSize="small" />,
      color: '#43a047',
      description: 'This month\'s environmental heroes'
    },
    {
      label: 'All Time',
      period: 'alltime',
      icon: <AllInclusive fontSize="small" />,
      color: '#2e7d32',
      description: 'Hall of Fame - Greatest eco warriors'
    }
  ];

  // Fetch user stats if authenticated (EXACT working pattern)
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserStats();
    }
  }, [isAuthenticated]);

  // Fetch leaderboard data when tab changes (EXACT working pattern)
  useEffect(() => {
    const currentPeriod = tabs[currentTab].period;
    // Use correct default sorting based on period
    const defaultSort = currentPeriod === 'alltime' ? 'rewardsPoints,desc' : 'totalPoints,desc';
    setSortBy(defaultSort); // Update sortBy state to match period
    fetchLeaderboardData(currentPeriod, 1, pageSize, defaultSort);
  }, [currentTab, pageSize]); // Remove sortBy from dependencies to avoid infinite loop

  const fetchUserStats = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await getUserStats();
      setUserStats(response.data);
    } catch (error) {
      console.warn('Failed to fetch user stats:', error);
    }
  };

  const fetchLeaderboardData = async (period, page = 1, size = pageSize, sort = sortBy, showLoading = true) => {
    if (showLoading) {
      setLeaderboardData(prev => ({
        ...prev,
        [period]: { ...prev[period], loading: true, error: null }
      }));
    }

    try {
      const response = await getLeaderboard(period, page - 1, size, sort);
      const { content, totalPages, totalElements, number } = response.data;

      // Process data to mark current user (EXACT working pattern)
      const processedData = content.map(entry => ({
        ...entry,
        isCurrentUser: isAuthenticated && user &&
          (entry.userId === user.id || entry.displayName === user.displayName)
      }));

      setLeaderboardData(prev => ({
        ...prev,
        [period]: {
          data: processedData,
          loading: false,
          error: null,
          page: number + 1,
          totalPages,
          totalElements
        }
      }));

    } catch (error) {
      console.error(`Failed to fetch ${period} leaderboard:`, error);

      setLeaderboardData(prev => ({
        ...prev,
        [period]: {
          ...prev[period],
          loading: false,
          error: 'Unable to load leaderboard data. Please try again later.'
        }
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePageChange = (period, newPage) => {
    fetchLeaderboardData(period, newPage, pageSize, sortBy);
  };

  const handlePageSizeChange = (period, newSize) => {
    setPageSize(newSize);
    fetchLeaderboardData(period, 1, newSize, sortBy);
  };

  const handleSortChange = (period, newSort) => {
    setSortBy(newSort);
    fetchLeaderboardData(period, 1, pageSize, newSort);
  };

  const handleRefresh = () => {
    const currentPeriod = tabs[currentTab].period;
    const currentData = leaderboardData[currentPeriod];
    fetchLeaderboardData(currentPeriod, currentData.page, pageSize, sortBy, false);
    if (isAuthenticated) {
      fetchUserStats();
    }
  };

  const currentPeriod = tabs[currentTab].period;
  const currentData = leaderboardData[currentPeriod];
  const currentTabConfig = tabs[currentTab];

  // Utility function for email masking
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    if (username.length <= 2) return email;
    return `${username[0]}${'*'.repeat(Math.min(username.length - 2, 3))}${username[username.length - 1]}@${domain}`;
  };

  // Memoized user info component
  const renderCurrentUserInfo = () => {
    if (!user) return null;

    const stats = userStats || {};

    return (
      <Card elevation={3} sx={{
        mb: 3,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.95)',
        border: `2px solid ${currentTabConfig.color}50`
      }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Avatar
                src={user.profilePhotoPath}
                sx={{
                  width: 60,
                  height: 60,
                  border: `3px solid ${currentTabConfig.color}`
                }}
              >
                {user.displayName?.charAt(0)}
              </Avatar>
            </Grid>
            <Grid item flex={1}>
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                    {user.displayName} (You)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalPoints || user.rewardsPoints || 0} Points
                  </Typography>
                </Box>

                <Chip
                  icon={<EmojiNature />}
                  label={stats.currentLevel || 'Rookie'}
                  size="small"
                  sx={{
                    background: '#e8f5e8',
                    color: '#2e7d32',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    height: 28,
                    border: '2px solid #4caf50'
                  }}
                />

                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<span>♻️</span>}
                    label={`${stats.totalClassifications || 0} Classifications`}
                    size="small"
                    sx={{
                      background: '#43a04720',
                      color: '#43a047',
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                  <Chip
                    icon={<span>🏅</span>}
                    label={`${stats.totalBadges || 0} Badges`}
                    size="small"
                    sx={{
                      background: '#ff980020',
                      color: '#ff9800',
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                  {stats.currentStreak > 0 && (
                    <Chip
                      icon={<span>🔥</span>}
                      label={`${stats.currentStreak} day streak`}
                      size="small"
                      sx={{
                        background: '#ff572220',
                        color: '#ff5722',
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Paper elevation={6} sx={{
      borderRadius: 4,
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)'
    }}>
      {/* Header Section */}
      <Box sx={{
        background: `linear-gradient(135deg, ${currentTabConfig.color}20 0%, ${currentTabConfig.color}30 100%)`,
        p: 4,
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <EmojiEvents sx={{ fontSize: 40, color: currentTabConfig.color }} />
          <Box flex={1}>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#1b5e20' }}>
              🏆 Eco Champions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {currentTabConfig.description}
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={currentData.loading}
                sx={{
                  background: 'rgba(255,255,255,0.9)',
                  color: currentTabConfig.color,
                  '&:hover': { background: 'rgba(255,255,255,1)' },
                  '&:disabled': { background: 'rgba(255,255,255,0.5)' }
                }}
              >
                <Refresh className={currentData.loading ? 'rotating' : ''} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Current User Info */}
        {renderCurrentUserInfo()}

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              minHeight: 60,
              borderRadius: 2,
              mx: 0.5,
              transition: 'all 0.3s ease',
              color: '#2e7d32'
            },
            '& .Mui-selected': {
              background: 'rgba(255,255,255,0.8)',
              color: currentTabConfig.color + ' !important',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.period}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Controls Section */}
      <Box sx={{ p: 3, background: 'rgba(255,255,255,0.9)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => handleSortChange(currentPeriod, e.target.value)}
                label="Sort By"
              >
                {currentTab === 2 ? [
                  <MenuItem key="rewardsPoints-desc" value="rewardsPoints,desc">🏆 Highest Points</MenuItem>,
                  <MenuItem key="rewardsPoints-asc" value="rewardsPoints,asc">📈 Lowest Points</MenuItem>,
                  <MenuItem key="displayName-asc" value="displayName,asc">🔤 Name A-Z</MenuItem>,
                  <MenuItem key="displayName-desc" value="displayName,desc">🔤 Name Z-A</MenuItem>,
                  <MenuItem key="createdAt-desc" value="createdAt,desc">📅 Newest First</MenuItem>,
                  <MenuItem key="createdAt-asc" value="createdAt,asc">📅 Oldest First</MenuItem>
                ] : [
                  <MenuItem key="totalPoints-desc" value="totalPoints,desc">🏆 Highest Points</MenuItem>,
                  <MenuItem key="totalPoints-asc" value="totalPoints,asc">📈 Lowest Points</MenuItem>,
                  <MenuItem key="displayName-asc" value="displayName,asc">🔤 Name A-Z</MenuItem>,
                  <MenuItem key="displayName-desc" value="displayName,desc">🔤 Name Z-A</MenuItem>
                ]}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Items per Page</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(currentPeriod, e.target.value)}
                label="Items per Page"
              >
                <MenuItem value={5}>5 champions</MenuItem>
                <MenuItem value={10}>10 champions</MenuItem>
                <MenuItem value={20}>20 champions</MenuItem>
                <MenuItem value={50}>50 champions</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" textAlign="right">
              🌍 Showing {currentData.data.length} of {currentData.totalElements} eco champions
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Leaderboard Cards */}
      <Box sx={{ p: 3 }}>
        {currentData.loading && (
          <LinearProgress sx={{ borderRadius: 2, height: 8, mb: 2 }} />
        )}
        {currentData.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {currentData.error}
          </Alert>
        )}

        {currentData.data.length === 0 && !currentData.loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <EmojiEvents sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No champions yet for this period
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Be the first to start your eco journey!
            </Typography>
          </Box>
        ) : (
          <Fade in={true} timeout={600}>
            <Stack spacing={3} sx={{ mb: 4 }}>
              {currentData.data.map((entry, index) => {
                const actualRank = entry.rank || ((currentData.page - 1) * pageSize + index + 1);

                return (
                  <Grow
                    key={`${entry.userId || entry.displayName || index}-${currentPeriod}-${currentData.page}`}
                    in={true}
                    timeout={300 + (index * 100)}
                    style={{ transformOrigin: '0 0 0' }}
                  >
                    <div>
                      <LeaderboardCard
                        entry={entry}
                        rank={actualRank}
                        showDetailedStats={isAuthenticated}
                        isCurrentUser={entry.isCurrentUser || false}
                        animated={true}
                      />
                    </div>
                  </Grow>
                );
              })}
            </Stack>
          </Fade>
        )}

        {/* Pagination */}
        {currentData.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={currentData.totalPages}
              page={currentData.page}
              onChange={(e, newPage) => handlePageChange(currentPeriod, newPage)}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 700,
                  border: '2px solid transparent',
                  '&:hover': {
                    border: `2px solid ${currentTabConfig.color}50`
                  }
                },
                '& .Mui-selected': {
                  background: `linear-gradient(135deg, ${currentTabConfig.color} 0%, ${currentTabConfig.color}dd 100%) !important`,
                  color: 'white !important',
                  border: `2px solid ${currentTabConfig.color} !important`
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default RewardsLeaderboard;
